import { createIconElement, PAYMENT_STATUS, stanNotify } from '@/lib';
import IcWarning from '@/assets/icons/ic-warning-circle.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';
import { StanButton } from '@/components';
import { ProductPayment } from '../types/transactionTypes';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useSendPaymentFailedReminder } from '@/queries/useIncome';

interface RetryingPaymentBannerProps {
    paymentData: ProductPayment;
    onRefreshData?: () => void;
}

export function RetryingPaymentBanner({ paymentData, onRefreshData }: RetryingPaymentBannerProps) {
    const { paymentId } = useParams();
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // Payment is retrying if status is PENDING with retrying flag, or FAILED with retrying flag
    const isRetrying =
        (paymentData.status === PAYMENT_STATUS.PENDING || paymentData.status === PAYMENT_STATUS.FAILED) &&
        paymentData.data.retrying;

    const { mutate: sendReminder, isPending: isSendingReminder } = useSendPaymentFailedReminder();

    if (!isRetrying) {
        return null;
    }

    const customerName = paymentData.full_name || paymentData.data.name || 'Customer';
    const retryDate = paymentData.data.next_payment_attempt
        ? new Date(paymentData.data.next_payment_attempt * 1000)
        : null;

    // Calculate retry time text - if no retry_date, default to 4 days (common retry interval)
    let retryTimeText = '4 days';
    if (retryDate && retryDate > new Date()) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const retryDay = new Date(retryDate.getFullYear(), retryDate.getMonth(), retryDate.getDate());

        if (retryDay.getTime() === today.getTime()) {
            retryTimeText = 'today';
        } else if (retryDay.getTime() === tomorrow.getTime()) {
            retryTimeText = 'tomorrow';
        } else {
            const days = Math.ceil((retryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            retryTimeText = `${days} ${days === 1 ? 'day' : 'days'}`;
        }
    }

    const WarningIcon = createIconElement(IcWarning, {
        className: 'text-negative-active',
    });
    const PlusIcon = createIconElement(IcPlus);

    const handleSendReminder = () => {
        if (paymentId && !isButtonDisabled) {
            sendReminder(paymentId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Reminder Sent',
                        text: 'Payment reminder has been sent to the customer.',
                        duration: 3000,
                    });
                    onRefreshData?.();
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Failed to send reminder',
                        text: error?.response?.data?.message || 'Please try again later.',
                        duration: 5000,
                    });
                },
            });
            setIsButtonDisabled(true);

            // Re-enable button after 10 seconds
            setTimeout(() => {
                setIsButtonDisabled(false);
            }, 10000);
        }
    };

    return (
        <div className='bg-neutral-subtle text-dark mt-6 w-full rounded-2xl border-0 p-5 text-sm'>
            <div className='flex items-center gap-3'>
                <div className='flex size-5 shrink-0 items-center'>{WarningIcon}</div>
                <div className='flex grow flex-col gap-3 md:flex-row md:items-center md:gap-3'>
                    <span className='flex-1'>
                        <strong>{customerName}&apos;s</strong> credit card has failed once. We&apos;ll retry their
                        payment again <strong>{retryTimeText}</strong>. In the meantime send them a reminder to update
                        their payment details.
                    </span>
                    <StanButton
                        name='send-payment-reminder-button'
                        color='primary'
                        size='sm'
                        onClick={handleSendReminder}
                        loading={isSendingReminder}
                        disabled={isButtonDisabled}
                        icon={PlusIcon}
                        className='w-full shrink-0 md:w-auto'
                    >
                        Send Reminder
                    </StanButton>
                </div>
            </div>
        </div>
    );
}
